import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import "./InvestmentPurchaseTotalViewModal.scss";

const TRANSACTION_TYPE_LABELS = {
  1: "Vendor",
  2: "Import",
};

function formatMoney(value) {
  return Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDateValue(value) {
  if (!value) return "-";
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleDateString("en-GB");
  return String(value);
}

function MetaItem({ label, value }) {
  return (
    <Box className="iptvm__meta-item">
      <Typography variant="caption" className="iptvm__meta-label">
        {label}
      </Typography>
      <Typography variant="body2" className="iptvm__meta-value">
        {value ?? "-"}
      </Typography>
    </Box>
  );
}

function SectionCard({ title, children }) {
  return (
    <Box className="iptvm__section-card">
      <Typography variant="subtitle2" className="iptvm__section-title">
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export default function InvestmentPurchaseTotalViewModal({ show, onHide, data, loading = false }) {
  const title = data?.InvoiceNr
    ? `Detajet e Fatures #${data.InvoiceNr}`
    : "Detajet e Blerjes Investive";

  const paymentLabel = (() => {
    if (data?.PaymentOptions === 0 || data?.PaymentOptions === "0") return "Pagesa Behet me vone";
    return data?.PaymentTypeName || data?.PaymentType || "-";
  })();

  const transactionTypeLabel = TRANSACTION_TYPE_LABELS[Number(data?.TransactionType)] || "-";

  const amount0 = Number(data?.Amount0 || 0);
  const amount8 = Number(data?.Amount8 || 0);
  const amount18 = Number(data?.Amount18 || 0);
  const vat8 = Number(data?.VAT8 || 0);
  const vat18 = Number(data?.VAT18 || 0);
  const total0 = Number(data?.Total0 || amount0);
  const total8 = Number(data?.Total8 || amount8 + vat8);
  const total18 = Number(data?.Total18 || amount18 + vat18);
  const totalNoVat = Number(data?.AmountWithoutVAT || amount0 + amount8 + amount18);
  const totalVat = Number(data?.VATTotal || vat8 + vat18);
  const totalWithVat = Number(data?.Total || totalNoVat + totalVat);

  const paymentStatusPaid = !(data?.PaymentOptions === 0 || data?.PaymentOptions === "0");

  return (
    <Dialog
      open={show}
      onClose={onHide}
      maxWidth="md"
      fullWidth
      PaperProps={{ className: "iptvm__paper" }}
    >
      <Box className="iptvm__hero">
        <Box>
          <Typography variant="h6" className="iptvm__hero-title">
            {title}
          </Typography>
          <Typography variant="body2" className="iptvm__hero-subtitle">
            Shikim i te dhenave pa mundesi ndryshimi
          </Typography>
        </Box>
        <Box className="iptvm__hero-chips">
          <Chip
            label={paymentStatusPaid ? "Paguar" : "Pa Paguar"}
            color={paymentStatusPaid ? "success" : "error"}
            variant="filled"
            className="iptvm__chip-status"
          />
          <Chip
            label={"Lloji: " + transactionTypeLabel}
            className="iptvm__chip-type"
          />
        </Box>
      </Box>

      <DialogContent dividers className="iptvm__content">
        {loading ? (
          <Box className="iptvm__loading">
            <CircularProgress size={28} />
            <Typography variant="body2" className="iptvm__loading-text">
              Duke marre te dhenat e fatures...
            </Typography>
          </Box>
        ) : (
          <Box className="iptvm__grid">
            <SectionCard title="Detaje te Fatures">
              <Box className="iptvm__top-cards">
                {[
                  { label: "Numri i Fatures", value: data?.InvoiceNr || "-" },
                  { label: "Data e Fatures", value: formatDateValue(data?.Date) },
                  { label: "Furnitori", value: data?.AccountName || "-" },
                ].map((item) => (
                  <Box key={item.label} className="iptvm__info-card">
                    <Typography variant="caption" className="iptvm__info-card-label">
                      {item.label}
                    </Typography>
                    <Typography variant="body2" className="iptvm__info-card-value">
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box className="iptvm__meta-grid">
                <Box className="iptvm__meta-card">
                  <Typography variant="caption" className="iptvm__meta-card-title">
                    Informata Kontabel
                  </Typography>
                  <MetaItem label="Aseti" value={data?.AssetName || "-"} />
                  <MetaItem label="Lloji i Blerjes" value={transactionTypeLabel} />
                  <MetaItem label="Data e Pageses" value={formatDateValue(data?.PayDate)} />
                </Box>

                <Box className="iptvm__meta-card">
                  <Typography variant="caption" className="iptvm__meta-card-title">
                    Informata te Pageses
                  </Typography>
                  <MetaItem label="Menyra Pageses" value={paymentLabel} />
                  <MetaItem label="Statusi i Pageses" value={paymentStatusPaid ? "Paguar" : "Pa Paguar"} />
                  <MetaItem label="Tipi i Transaksionit" value={transactionTypeLabel} />
                </Box>
              </Box>

              <Box className="iptvm__comment">
                <Typography variant="caption" className="iptvm__comment-label">
                  Komenti
                </Typography>
                <Typography variant="body2" className="iptvm__comment-value">
                  {data?.Coment || data?.Comment || "-"}
                </Typography>
              </Box>
            </SectionCard>

            <SectionCard title="Vlerat dhe TVSH">
              <Box className="iptvm__vat-table">
                <Box className="iptvm__vat-head">
                  {["Norma TVSH", "Vlera pa TVSH", "Vlera e TVSH", "Total"].map((h) => (
                    <Typography key={h} variant="caption" className="iptvm__vat-head-cell">{h}</Typography>
                  ))}
                </Box>

                {[
                  { rate: "18%", amount: amount18, vat: vat18, total: total18 },
                  { rate: "8%", amount: amount8, vat: vat8, total: total8 },
                  { rate: "0%", amount: amount0, vat: 0, total: total0 },
                ].map((row, index) => (
                  <Box key={row.rate} className={"iptvm__vat-row" + (index % 2 === 1 ? " iptvm__vat-row--alt" : "")}>
                    <Typography variant="body2" className="iptvm__vat-rate">{row.rate}</Typography>
                    <Typography variant="body2" className="iptvm__vat-value">{formatMoney(row.amount)} EUR</Typography>
                    <Typography variant="body2" className="iptvm__vat-value">{formatMoney(row.vat)} EUR</Typography>
                    <Typography variant="body2" className="iptvm__vat-total">{formatMoney(row.total)} EUR</Typography>
                  </Box>
                ))}
              </Box>
            </SectionCard>

            <SectionCard title="Totale">
              <Box className="iptvm__totals-grid">
                {[
                  { label: "Total pa TVSH", value: totalNoVat, tone: "#1d4ed8" },
                  { label: "Total TVSH", value: totalVat, tone: "#0f766e" },
                  { label: "Total me TVSH", value: totalWithVat, tone: "#284945" },
                ].map((item) => (
                  <Box key={item.label} className="iptvm__total-card">
                    <Typography variant="caption" className="iptvm__total-label">{item.label}</Typography>
                    <Typography variant="h6" className="iptvm__total-value" style={{ color: item.tone }}>
                      {formatMoney(item.value)} EUR
                    </Typography>
                  </Box>
                ))}
              </Box>
            </SectionCard>
          </Box>
        )}
      </DialogContent>

      <DialogActions className="iptvm__actions">
        <Button
          onClick={onHide}
          variant="contained"
          sx={{
            background: "linear-gradient(135deg, #1b4e57 0%, #284945 100%)",
            px: 3.5,
            fontWeight: 700,
            borderRadius: 2,
            "&:hover": {
              background: "linear-gradient(135deg, #153f46 0%, #203c39 100%)",
            },
          }}
        >
          Mbyll
        </Button>
      </DialogActions>
    </Dialog>
  );
}
